import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchInvoiceDelivery, fetchJobInvoicePdf, fetchWorkOrderInvoicePdf, sendInvoiceEmail } from './api';
import { formatDateTimeDisplay } from '../utils/timezone';
import './SendInvoiceDialog.css';

// Shows a job's or work order's QuickBooks invoice with who it goes to (To, Cc, Bcc), then has
// QuickBooks email it. onSent gets the server's reply ({ invoice_sent_at, invoice_sent_to, ... }).
const SendInvoiceDialog = ({ isJob = false, id, invoiceNumber, onClose, onSent }) => {
  const [recipients, setRecipients] = useState({ email: '', cc: '', bcc: '' });
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchInvoiceDelivery(isJob, id)
      .then((data) => {
        if (cancelled) return;
        setDelivery(data);
        // The invoice's own Cc/Bcc, else the company's defaults from QuickBooks
        setRecipients({ email: data.email || '', cc: data.cc || data.company_cc || '', bcc: data.bcc || data.company_bcc || '' });
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not read the invoice from QuickBooks.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isJob, id]);

  // The invoice as QuickBooks prints it, for the preview
  useEffect(() => {
    let cancelled = false;
    let url = null;
    (isJob ? fetchJobInvoicePdf(id) : fetchWorkOrderInvoicePdf(id))
      .then((pdf) => {
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch((err) => {
        if (!cancelled) setPdfError(err.response?.data?.error || 'Could not load the invoice preview.');
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [isJob, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRecipients((prev) => ({ ...prev, [name]: value }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // React passes portal events up to the job or work order form
    setSending(true);
    setError('');
    try {
      const data = await sendInvoiceEmail(isJob, id, recipients);
      setSent(data);
      if (onSent) onSent(data);
    } catch (err) {
      setError(err.response?.data?.error || 'QuickBooks could not send the invoice.');
    } finally {
      setSending(false);
    }
  };

  // Emailed before, from the app or from QuickBooks itself
  const lastSentAt = delivery && (delivery.invoice_sent_at || delivery.emailed_at);
  const alreadySent = delivery && (delivery.emailed || delivery.invoice_sent_at);
  const number = sent?.invoice_number || delivery?.invoice_number || invoiceNumber;
  const ccFromCompany = delivery && !delivery.cc && delivery.company_cc && recipients.cc === delivery.company_cc;
  const bccFromCompany = delivery && !delivery.bcc && delivery.company_bcc && recipients.bcc === delivery.company_bcc;

  const field = (name, label, placeholder, note) => (
    <>
      <label className="send-invoice-label" htmlFor={`send-invoice-${name}`}>{label}</label>
      <input
        id={`send-invoice-${name}`}
        name={name}
        className="send-invoice-input"
        type="text"
        inputMode="email"
        autoComplete="off"
        value={recipients[name]}
        onChange={handleChange}
        placeholder={loading ? 'Loading…' : placeholder}
        disabled={loading || sending}
      />
      {note && <small className="send-invoice-help">{note}</small>}
    </>
  );

  return createPortal(
    <div className="send-invoice-overlay" onClick={(e) => e.stopPropagation()}>
      <form className="send-invoice-dialog" onSubmit={handleSend}>
        <h3>Email Invoice {number ? `#${number}` : ''}</h3>

        <div className="send-invoice-body">
          <div className="send-invoice-preview">
            {pdfUrl ? (
              <>
                <iframe src={`${pdfUrl}#navpanes=0&view=FitH`} title={`Invoice ${number || ''}`} className="send-invoice-pdf" />
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="send-invoice-open">
                  Open the full invoice
                </a>
              </>
            ) : (
              <div className="send-invoice-preview-placeholder">
                {pdfError || 'Loading the invoice…'}
              </div>
            )}
          </div>

          <div className="send-invoice-fields">
            {sent ? (
              <div className="send-invoice-success">
                Invoice {number ? `#${number} ` : ''}was emailed from QuickBooks to {sent.invoice_sent_to}
                {sent.cc ? `, Cc ${sent.cc}` : ''}{sent.bcc ? `, Bcc ${sent.bcc}` : ''}.
              </div>
            ) : (
              <>
                {alreadySent && (
                  <div className="send-invoice-warning">
                    This invoice was already emailed
                    {lastSentAt ? ` on ${formatDateTimeDisplay(lastSentAt)}` : ''}
                    {delivery.invoice_sent_to ? ` to ${delivery.invoice_sent_to}` : ''}. Send it again?
                  </div>
                )}
                {delivery?.delivery_error && (
                  <div className="send-invoice-warning">
                    QuickBooks says the last email could not be delivered ({delivery.delivery_error}). Check the address.
                  </div>
                )}
                {delivery && (
                  <div className="send-invoice-amounts">
                    Total ${(parseFloat(delivery.total) || 0).toFixed(2)}
                    {parseFloat(delivery.balance) < parseFloat(delivery.total) &&
                      ` · $${(parseFloat(delivery.balance) || 0).toFixed(2)} still owed`}
                  </div>
                )}
                {field('email', 'To', 'customer@example.com')}
                {field('cc', 'Cc', 'No Cc', ccFromCompany && 'From your QuickBooks company email settings.')}
                {field('bcc', 'Bcc', 'No Bcc', bccFromCompany && 'From your QuickBooks company email settings.')}
                {delivery?.copy_to_company && (
                  <small className="send-invoice-help">
                    QuickBooks also sends a copy to {delivery.company_email || 'your company email'}.
                  </small>
                )}
                <small className="send-invoice-help">
                  Separate several addresses with commas. Changes are also saved on the invoice in QuickBooks.
                </small>
                {delivery?.email_subject && (
                  <details className="send-invoice-message">
                    <summary>Email QuickBooks will send</summary>
                    <div className="send-invoice-subject">{delivery.email_subject}</div>
                    <div className="send-invoice-message-body">{delivery.email_message}</div>
                    <small className="send-invoice-help">Change this wording in QuickBooks (Settings → Sales → Messages).</small>
                  </details>
                )}
              </>
            )}
          </div>
        </div>

        {error && <div className="send-invoice-error">{error}</div>}

        <div className="send-invoice-buttons">
          {sent ? (
            <button type="button" className="send-invoice-primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button type="button" className="send-invoice-cancel" onClick={onClose} disabled={sending}>Cancel</button>
              <button type="submit" className="send-invoice-primary" disabled={loading || sending || !recipients.email.trim()}>
                {sending ? 'Sending…' : alreadySent ? 'Send Again' : 'Send Invoice'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>,
    document.body
  );
};

export default SendInvoiceDialog;
